/**
 * Single plan card for the in-app paywall. Renders title, price, features, and CTA.
 * Supports recommended badge (Owner-Operator), custom CTA text, and "Best for you" when suggestFleet.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import {
  FLEET_BASE_MONTHLY_DOLLARS,
  FLEET_PER_VEHICLE_MONTHLY_DOLLARS,
  fleetMonthlyTotalDollars,
} from '../../config/pricingPublic';
import '../../App.css';

const FLEET_QTY_MIN = 1;
const FLEET_QTY_MAX = 25;

const PricingCard = ({
  title,
  price,
  period,
  blurb,
  features,
  tier,
  currentTier,
  onSelect,
  loading,
  recommended = false,
  ctaText = 'Get Started',
  ctaTo,
  vehicleLimit,
  priceAddon,
  suggestFleet = false,
}) => {
  const [truckQty, setTruckQty] = useState(FLEET_QTY_MIN);
  const isCurrent = currentTier === tier;
  const isFleet = tier === 'fleet';

  return (
    <div
      className={`pricing-card paywall-card ${recommended ? 'paywall-card--recommended' : ''} ${isCurrent ? 'current' : ''} ${suggestFleet ? 'paywall-card--suggested' : ''}`}
    >
      {recommended && (
        <div className="paywall-badge" aria-hidden>
          Recommended
        </div>
      )}
      {suggestFleet && !recommended && (
        <div className="paywall-badge paywall-badge--suggest" aria-hidden>
          Best for you
        </div>
      )}
      <div className="pricing-card-header">
        <h3>{title}</h3>
        {vehicleLimit != null && (
          <p className="paywall-card-vehicle-note">Up to {vehicleLimit} vehicles</p>
        )}
        <div className="pricing-price">
          <span className="price-amount">{price}</span>
          <span className="price-period">/{period}</span>
          {priceAddon ? (
            <span className="price-addon">{priceAddon}</span>
          ) : null}
        </div>
        {blurb ? <p className="paywall-card-blurb">{blurb}</p> : null}
      </div>
      <ul className="pricing-features">
        {features.map((feature, idx) => (
          <li key={idx}>
            <Check size={18} color="#10B981" aria-hidden />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      {isFleet && (
        <div className="form-group" style={{ margin: '16px 0 0 0' }}>
          <label
            htmlFor="paywall-trucks-to-track"
            style={{ color: '#e5e5e5' }}
          >
            Trucks to track
          </label>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 8,
            }}
          >
            <button
              type="button"
              className="btn btn-secondary"
              aria-label="Fewer trucks"
              disabled={loading || isCurrent || truckQty <= FLEET_QTY_MIN}
              onClick={() =>
                setTruckQty((n) => Math.max(FLEET_QTY_MIN, n - 1))
              }
            >
              −
            </button>
            <span
              id="paywall-trucks-to-track"
              aria-live="polite"
              style={{
                minWidth: 28,
                textAlign: 'center',
                fontWeight: 700,
                color: '#f5f5f5',
              }}
            >
              {truckQty}
            </span>
            <button
              type="button"
              className="btn btn-secondary"
              aria-label="More trucks"
              disabled={loading || isCurrent || truckQty >= FLEET_QTY_MAX}
              onClick={() =>
                setTruckQty((n) => Math.min(FLEET_QTY_MAX, n + 1))
              }
            >
              +
            </button>
          </div>
          <p className="paywall-card-vehicle-note" style={{ margin: 0 }}>
            ${FLEET_BASE_MONTHLY_DOLLARS} base + {truckQty} trucks × $
            {FLEET_PER_VEHICLE_MONTHLY_DOLLARS} = $
            {fleetMonthlyTotalDollars(truckQty)}/mo
          </p>
        </div>
      )}
      {ctaTo && !isCurrent ? (
        <Link to={ctaTo} className="button primary full-width paywall-cta">
          {ctaText}
        </Link>
      ) : (
        <button
          className="button primary full-width paywall-cta"
          onClick={() => onSelect(tier, isFleet ? truckQty : undefined)}
          disabled={loading || isCurrent}
        >
          {loading ? 'Loading...' : isCurrent ? 'Current Plan' : ctaText}
        </button>
      )}
    </div>
  );
};

export default PricingCard;
